import { TV2M_API_BASE_PATH } from '@apm/contracts';
import type { TriggerRequest, TriggerResponse } from '@apm/contracts';

/**
 * `POST /v1/trigger` 클라이언트 (`docs/08` §2).
 *
 * **인증이 없다.** 재생 클라이언트는 파트너사 기기라 API Key 를 나눠 줄 수 없고, 서버가
 * 이 엔드포인트만 공개로 둔 이유가 그것이다(T3). 그래서 여기에는 헤더가 붙지 않는다 —
 * 모바일 웹의 `FetchNetworkAdapter` 와 다른 점이다.
 *
 * `credentials` 도 붙이지 않는다. tv2m-server 는 `enableCors` 를 `credentials: false` 로
 * 띄우므로(T1) 실으면 CORS 가 조용히 막는다.
 *
 * 요청·응답 타입을 여기서 다시 선언하지 않는다 (절대 규칙 10) — `@apm/contracts` 의
 * `TriggerRequest`/`TriggerResponse` 가 서버 DTO 와 같은 출처다.
 */

/**
 * 타임아웃 3초 (`docs/09` T8).
 *
 * **재생을 멈추지 않는 것이 이 상수의 목적이다.** 시청자는 버튼을 누르고 화면 앞에 있고,
 * 서버가 답하지 않는다고 영원히 「요청 중…」을 보게 둘 수는 없다. 초과하면 오류 오버레이로
 * 떨어뜨리고 영상은 계속 간다 (절대 규칙 7·14).
 *
 * 3초는 서버가 BLOOM Server 를 되부르는 시간(리졸버 타임아웃 3초, T3)과 같은 크기다 — 그보다
 * 짧게 잡으면 정상 응답을 우리가 먼저 포기한다.
 */
export const TRIGGER_TIMEOUT_MS = 3000;

export class TriggerError extends Error {
  constructor(
    readonly kind: 'timeout' | 'network' | 'status',
    message: string,
  ) {
    super(message);
    this.name = 'TriggerError';
  }
}

/**
 * 트리거를 부른다. 실패는 전부 `TriggerError` 다 — 호출부가 한 가지 모양만 다루면 된다.
 *
 * `AbortSignal.timeout()` 을 쓰지 않는다. 그것이 던지는 것은 `TimeoutError` 라 `AbortError`
 * 와 구분해야 하고, 무엇보다 **왜 실패했는지를 우리가 이름 붙여 두는 편**이 디버그 패널에
 * 그대로 나간다 (T8 이 마지막 handoff 결과를 패널에 노출한다).
 */
export async function requestTv2mTrigger(
  serverUrl: string,
  body: TriggerRequest,
  timeoutMs: number = TRIGGER_TIMEOUT_MS,
): Promise<TriggerResponse> {
  const base = serverUrl.replace(/\/+$/, '');
  return postHandoffJson<TriggerResponse>(`${base}${TV2M_API_BASE_PATH}/trigger`, body, {
    timeoutMs,
  });
}

/**
 * 핸드오프 요청 한 번. **두 모드가 이 함수를 함께 쓴다** — delegated 는 위 `/v1/trigger`,
 * federated 는 BLOOM Server 의 `/v1/bloom/handoff`(`bloom-handoff.ts`).
 *
 * **실패 분류가 한 곳에 있어야 하는 이유가 그것이다.** 타임아웃·단절·상태 코드를 두 벌로
 * 만들면 모드에 따라 같은 실패가 다른 문구로 나가고, 그 차이는 배포 설정에서 온다 —
 * 절대 규칙 17 이 프런트에서 깨지는 모양이 정확히 이것이다.
 */
export async function postHandoffJson<T>(
  url: string,
  body: unknown,
  options: { timeoutMs?: number; headers?: Record<string, string> } = {},
): Promise<T> {
  const timeoutMs = options.timeoutMs ?? TRIGGER_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (error) {
    // 중단이 우리 타이머 때문인지 다른 이유인지 가른다. 화면 문구가 달라진다 —
    // 타임아웃은 "다시 시도" 이고 네트워크 단절은 "연결을 확인" 이다.
    if (controller.signal.aborted) {
      throw new TriggerError('timeout', `핸드오프 응답이 ${String(timeoutMs)}ms 안에 오지 않았다`);
    }
    throw new TriggerError('network', describe(error));
  } finally {
    // **성공 경로에서도 반드시 지운다.** 안 지우면 3초 뒤 `abort()` 가 이미 끝난 요청에
    // 걸리는데, 그 자체는 무해해도 다음 트리거를 위해 만든 컨트롤러와 헷갈릴 여지를 남긴다.
    clearTimeout(timer);
  }

  if (!response.ok) {
    throw new TriggerError('status', `핸드오프 실패: ${String(response.status)}`);
  }
  return (await response.json()) as T;
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
